import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { formatYearMonth } from '@/lib/datetime';
import { aneelCaseParams, aneelConsistencyParams, fetchAneelCase } from '@/services/aneel';
import type {
  AneelCaseDetail,
  AneelChargePosition,
  AneelCollectionRow,
  AneelComparable,
  AneelPenaltyWalk,
} from '@/services/types';

/**
 * One ANEEL infraction notice.
 *
 * The penalty history here is built from recorded EVENTS, not from the four
 * amount columns on the case. That is the difference between a chart that says
 * "nothing has happened yet" and one that draws three identical bars and reads
 * as "the penalty survived every stage" — because ANEEL carries the charged
 * amount into the final column while a case is undecided, so the columns are
 * always full and always look decided.
 *
 * The four state axes are repeated here rather than left on the list. A reader
 * can land on this URL directly, and "closed" on a case with no decision is
 * exactly the fact they would otherwise assume away.
 *
 * The header carries the facts a lawyer reads first -- when it was issued,
 * what was charged, what stands and whether that is an outcome -- and every
 * classifying fact on it links into the case list narrowed to that fact, so
 * "every notice of this nature" is one click and the same predicate the list
 * filters by. The strip and the export are the shared components every other
 * screen uses; this page had hand-rolled the one and lacked the other.
 */
export function AneelCaseDetailPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);
  const { key = '' } = useParams();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'case', key],
    queryFn: () => fetchAneelCase(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('aneel:case.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="space-y-2" data-case-header>
        <h1 className="text-lg font-semibold tabular-nums text-foreground">{data.notice_number}</h1>
        <p className="text-sm text-muted-foreground">
          {data.notice_date ? (
            <span data-notice-date>{t('aneel:case.header.issuedOn', { date: data.notice_date })}</span>
          ) : null}
          {data.trajectory ? (
            <span
              className="ml-2 rounded border px-1.5 py-0.5 text-xs text-foreground"
              data-trajectory={data.trajectory}
            >
              {t(`aneel:overview.trajectories.${data.trajectory}`, data.trajectory)}
            </span>
          ) : null}
        </p>
        {/* Each classifying fact opens the list narrowed to it -- by the SAME
            filter the list offers, so the count a reader lands on is the
            count that filter always gives. */}
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
          {data.entity_name ? (
            <HeaderLink
              href={entityHref(data.entity_cnpj)}
              title={t('aneel:case.header.openCompany')}
              label={data.entity_name}
            />
          ) : null}
          {data.nature ? (
            <HeaderLink
              href={`/aneel/cases?${aneelCaseParams({ nature: data.nature }).toString()}`}
              title={t('aneel:case.header.openNature')}
              label={data.nature}
            />
          ) : null}
          {data.inspecting_agency ? (
            <HeaderLink
              href={`/aneel/cases?${aneelCaseParams({ agency: data.inspecting_agency }).toString()}`}
              title={t('aneel:case.header.openAgency')}
              label={data.inspecting_agency}
            />
          ) : null}
          {data.bands.map((band) => (
            <HeaderLink
              key={band}
              href={`/aneel/cases?${aneelCaseParams({ band }).toString()}`}
              title={t('aneel:case.header.openBand')}
              label={band}
            />
          ))}
        </p>
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">{t('aneel:case.header.charged')}</dt>
            <dd className="tabular-nums text-foreground" data-charged>
              {data.original_amount === null ? '—' : currency.format(data.original_amount)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t('aneel:case.header.standing')}</dt>
            {/* The most dangerous number on the page, labelled per case:
                ANEEL carries the charge into this column while the notice
                is undecided, so on an undecided case it is a demand. */}
            <dd className="tabular-nums" data-standing data-outcome={String(data.decided)}>
              {data.final_amount === null ? (
                '—'
              ) : data.decided ? (
                <span className="text-foreground">{currency.format(data.final_amount)}</span>
              ) : (
                <span className="text-muted-foreground" title={t('aneel:cases.chargedNotFinal')}>
                  {currency.format(data.final_amount)}{' '}
                  <span className="text-[0.65rem] uppercase tracking-wide">
                    {t('aneel:case.header.chargedNotFinal')}
                  </span>
                </span>
              )}
            </dd>
          </div>
        </dl>
      </header>

      <ExplainerCard i18nKey="aneel:case.explainer" />

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/aneel/export/c/${encodeURIComponent(key)}`} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('aneel:case.state.title')}</h2>
        <StatePanel data={data} />
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:cases.guide.axes" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('aneel:case.walk.title')}</h2>
        {/* scope="record" throughout this page: every refusal below is about
            THIS CASE, not about what ANEEL as a corpus can support. Left at the
            default `corpus`, the badge reads "Indisponivel para esta agencia" --
            which tells a reader the agency supports none of this, on the
            strength of one case having no row. Measured 2026-09-09: that badge
            would be wrong on 1,207 of 1,569 cases for the decision record and
            999 for the comparables. Same defect already fixed on
            /aneel/e/:cnpj; this page was missed. */}
        <Answer answer={data.penalty_walk} help={t('aneel:case.walk.help')} scope="record">
          {(walk) => <PenaltyWalkPanel walk={walk} currency={currency} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="aneel:case.guide.walk" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:case.decision.title')}
        </h2>
        <Answer answer={data.decision_record} help={t('aneel:case.decision.help')} scope="record">
          {(record) => (
            <div className="space-y-2">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Instance
                  label={t('aneel:case.decision.first')}
                  codes={record.first_codes}
                  on={record.first_on}
                />
                <Instance
                  label={t('aneel:case.decision.board')}
                  codes={record.board_codes}
                  on={record.board_on}
                />
              </dl>
              {record.unknown_codes.length > 0 ? (
                <p className="text-xs text-muted-foreground" role="note">
                  {t('aneel:case.decision.unknown', { codes: record.unknown_codes.join(', ') })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <p className="mt-3 text-xs">
          <Link to="/aneel/appeals" className="text-primary underline-offset-4 hover:underline">
            {t('aneel:case.decision.appeals')}
          </Link>
        </p>
      </section>

      <section className="rounded-lg border bg-card p-4" data-charge-position>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:case.position.title')}
        </h2>
        <Answer answer={data.charge_position} help={t('aneel:case.position.help')} scope="record">
          {(position) => <ChargePositionPanel position={position} currency={currency} />}
        </Answer>
        <p className="mt-3 text-xs">
          <Link to="/aneel/penalties" className="text-primary underline-offset-4 hover:underline">
            {t('aneel:case.position.ladders')}
          </Link>
        </p>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">
            {t('aneel:case.collection.title')}
          </h2>
          {/* Counted here, not sent as a sentence from the backend. A prose
              summary composed server-side arrives in English and renders on a
              Portuguese page — which is precisely how the CADE refusals shipped
              untranslated and went unnoticed. */}
          <span className="text-xs text-muted-foreground">
            {t('aneel:case.collection.count', { count: data.collection.length })}
          </span>
        </div>
        {data.collection.length === 0 ? (
          <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            {t('aneel:case.collection.none')}
          </p>
        ) : (
          <div className="space-y-3">
            {/* Instalment grain, said before the rows rather than after. Several
                rows for one case is normal, and they can sit in different states
                at once — so there is deliberately no single "paid/unpaid" for the
                case, because picking one would be a judgement this application
                is not entitled to make. */}
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground">
              {t('aneel:case.collection.grain')}
            </p>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t('aneel:case.collection.reference')}
                    </th>
                    <th className="px-3 py-2 font-medium">{t('aneel:case.collection.credit')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:case.collection.status')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:case.collection.due')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('aneel:case.collection.paid')}
                    </th>
                    <th className="px-3 py-2 font-medium">{t('aneel:case.collection.dueOn')}</th>
                    <th className="px-3 py-2 font-medium">{t('aneel:case.collection.paidOn')}</th>
                    <th
                      className="px-3 py-2 text-right font-medium"
                      title={t('aneel:case.collection.daysLateHint')}
                    >
                      {t('aneel:case.collection.daysLate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.collection.map((row, index) => (
                    <CollectionRowView
                      key={`${row.collection_code}-${index}`}
                      row={row}
                      currency={currency}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <FieldGuide i18nKey="aneel:case.guide.collection" />
          </div>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:case.comparables.title')}
        </h2>
        <Answer answer={data.comparables} help={t('aneel:case.comparables.help')} scope="record">
          {(rows) => (
            <div className="space-y-3">
              <ol className="space-y-2">
                {rows.map((row) => (
                  <ComparableCard key={row.case_key} row={row} currency={currency} />
                ))}
              </ol>
              {/* The queue these pairs come from, narrowed to this nature: the
                  same `nature` predicate the queue's own filter applies. */}
              {data.nature ? (
                <Link
                  to={`/aneel/consistency?${aneelConsistencyParams({ nature: data.nature }).toString()}`}
                  className="inline-block text-sm text-primary underline-offset-2 hover:underline"
                  data-consistency-link
                >
                  {t('aneel:case.comparables.openQueue', { nature: data.nature })}
                </Link>
              ) : null}
              <FieldGuide i18nKey="aneel:case.guide.comparables" />
            </div>
          )}
        </Answer>
      </section>

      {data.notification ? (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('aneel:case.notification.title')}
          </h2>
          <p className="mb-2 text-xs text-muted-foreground">
            {t('aneel:case.notification.help')}
          </p>
          <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2" data-notification>
            <Row label={t('aneel:case.notification.number')} value={data.notification.notification_number} />
            <Row label={t('aneel:case.notification.date')} value={data.notification.notification_date} />
            <Row label={t('aneel:case.notification.received')} value={data.notification.reception_date} />
            <Row
              label={t('aneel:case.notification.manifested')}
              value={data.notification.manifestation_date}
            />
            <Row label={t('aneel:case.notification.type')} value={data.notification.decision_type} />
            <Row label={t('aneel:case.notification.decided')} value={data.notification.decision_date} />
            <Row label={t('aneel:case.notification.agent')} value={data.notification.agent_name} />
          </dl>
          <p className="mt-3 text-xs">
            <Link
              to="/aneel/notifications"
              className="text-primary underline-offset-4 hover:underline"
            >
              {t('aneel:case.notification.funnel')}
            </Link>
          </p>
        </section>
      ) : null}

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:case.record.title')}
        </h2>
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
          <Row label={t('aneel:case.record.process')} value={data.process_number} />
          <Row
            label={t('aneel:case.record.inspection')}
            value={data.inspection_process_number}
          />
          <Row
            label={t('aneel:case.record.cnpj')}
            value={data.entity_cnpj}
            href={entityHref(data.entity_cnpj)}
          />
          <Row label={t('aneel:case.record.classification')} value={data.legal_classification} />
          <Row label={t('aneel:case.record.closure')} value={data.closure_date} />
          <Row label={t('aneel:case.record.closureTerm')} value={data.closure_term} />
          <Row label={t('aneel:case.record.bands')} value={data.bands.join('; ') || null} />
          <Row label={t('aneel:case.record.object')} value={data.inspected_object} />
        </dl>
      </section>
    </div>
  );
}

/** A header fact that opens the list narrowed to it; plain text when it cannot. */
function HeaderLink({ href, title, label }: { href?: string; title: string; label: string }) {
  if (!href) return <span className="text-foreground">{label}</span>;
  return (
    <Link
      to={href}
      title={title}
      className="text-primary underline-offset-4 hover:underline"
      data-header-link
    >
      {label}
    </Link>
  );
}

function ChargePositionPanel({
  position,
  currency,
}: {
  position: AneelChargePosition;
  currency: Intl.NumberFormat;
}) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  // A percentile RANK, not a share: "p62" reads as the ladder does.
  const rank = (share: number) => `p${Math.round(share * 100)}`;

  return (
    <div className="space-y-2">
      <p className="text-sm text-foreground" data-corpus-position>
        {position.corpus_percentile === null
          ? t('aneel:case.position.corpusRefused', { n: number.format(position.corpus_n) })
          : t('aneel:case.position.corpus', {
              amount: currency.format(position.amount),
              percentile: rank(position.corpus_percentile),
              n: number.format(position.corpus_n),
            })}
      </p>
      <ul className="space-y-1 text-sm text-foreground">
        {position.bands.map((row) => (
          <li key={row.band} data-band-position={row.band} data-refused={String(row.percentile === null)}>
            {row.percentile === null
              ? t('aneel:case.position.bandRefused', { band: row.band, n: number.format(row.n) })
              : t('aneel:case.position.band', {
                  band: row.band,
                  percentile: rank(row.percentile),
                  n: number.format(row.n),
                })}
          </li>
        ))}
      </ul>
      <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
        {t('aneel:case.position.reading')}
      </p>
    </div>
  );
}

function StatePanel({ data }: { data: AneelCaseDetail }) {
  const { t } = useTranslation(['aneel']);
  const axes = [
    ['decided', data.decided],
    ['decisionRecorded', data.decision_recorded],
    ['closed', data.closed],
    ['collectionLinked', data.collection_linked],
  ] as const;

  // The contradiction, stated when it exists. It is the single most misread
  // thing about an ANEEL case and it is invisible in four independent badges.
  const contradiction =
    !data.decided && (data.closed || data.collection_linked || data.decision_recorded);

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {axes.map(([key, on]) => (
          <div
            key={key}
            className={
              on
                ? 'rounded-md border border-primary/40 bg-primary/10 p-3'
                : 'rounded-md border border-dashed p-3'
            }
          >
            <dt className="text-xs text-muted-foreground">{t(`aneel:cases.axes.${key}`)}</dt>
            <dd className="mt-1 text-sm font-medium text-foreground">
              {on ? t('aneel:case.state.yes') : t('aneel:case.state.no')}
            </dd>
          </div>
        ))}
      </dl>
      {contradiction ? (
        <p
          role="alert"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-foreground"
        >
          {t('aneel:case.state.undecidedButMoved')}
        </p>
      ) : null}
    </div>
  );
}

function PenaltyWalkPanel({
  walk,
  currency,
}: {
  walk: AneelPenaltyWalk;
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['aneel']);

  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {walk.stages.map((stage) => (
          <li
            key={stage.stage}
            className="flex flex-wrap items-baseline justify-between gap-2 rounded-md border bg-background p-3"
          >
            <span className="text-sm font-medium text-foreground">
              {t(`aneel:case.stages.${stage.stage}`, stage.stage)}
            </span>
            <span className="text-xs text-muted-foreground">{stage.occurred_on ?? '—'}</span>
            <span className="tabular-nums text-sm text-foreground">
              {stage.amount === null ? '—' : currency.format(stage.amount)}
            </span>
            <span
              className={
                stage.delta === null || stage.delta === 0
                  ? 'text-xs tabular-nums text-muted-foreground'
                  : 'text-xs font-medium tabular-nums text-foreground'
              }
            >
              {stage.delta === null
                ? ''
                : stage.delta === 0
                  ? t('aneel:case.walk.unchanged')
                  : currency.format(stage.delta)}
            </span>
          </li>
        ))}
      </ol>

      {/* An absent stage is NAMED, never drawn flat. "Not recorded" and
          "unchanged" are different facts and look identical on a chart. */}
      {walk.missing.length > 0 ? (
        <p className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
          {t('aneel:case.walk.missing', {
            stages: walk.missing
              .map((stage) => t(`aneel:case.stages.${stage}`, stage))
              .join(', '),
          })}
        </p>
      ) : null}
    </div>
  );
}

function Instance({
  label,
  codes,
  on,
}: {
  label: string;
  codes: string[];
  on: string | null;
}) {
  const { t } = useTranslation(['aneel']);

  return (
    <div className="rounded-md border bg-background p-3">
      <dt className="text-xs text-muted-foreground">
        {label} {on ? `· ${on}` : ''}
      </dt>
      <dd className="mt-1 text-sm text-foreground">
        {codes.length === 0
          ? t('aneel:case.decision.noneAtInstance')
          : codes.map((code) => t(`aneel:case.outcomes.${code}`, code)).join('; ')}
      </dd>
    </div>
  );
}

function CollectionRowView({
  row,
  currency,
}: {
  row: AneelCollectionRow;
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['aneel']);

  return (
    <tr className="border-t align-top">
      <td className="px-3 py-2 tabular-nums text-muted-foreground">
        {formatYearMonth(row.year_month)}{' '}
        {row.disambiguated_by_cnpj ? (
          <span className="ml-1 text-[0.65rem] uppercase" title={t('aneel:case.collection.byCnpj')}>
            {t('aneel:case.collection.byCnpjShort')}
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-foreground">{row.credit_status ?? '—'}</td>
      <td className="px-3 py-2 text-muted-foreground">{row.collection_status ?? '—'}</td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {row.amount_due === null ? '' : currency.format(row.amount_due)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {row.amount_paid === null ? '' : currency.format(row.amount_paid)}
      </td>
      <td className="px-3 py-2 tabular-nums text-muted-foreground">{row.due_date ?? '—'}</td>
      <td className="px-3 py-2 tabular-nums text-muted-foreground">{row.payment_date ?? '—'}</td>
      {/* Blank, never 0: a zero here is a payment on the day, and the source
          leaves the column empty on rows it never measured. */}
      <td className="px-3 py-2 text-right tabular-nums text-foreground" data-days-late>
        {row.days_late === null ? '' : row.days_late}
      </td>
    </tr>
  );
}

function ComparableCard({
  row,
  currency,
}: {
  row: AneelComparable;
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['aneel']);

  return (
    <li className="space-y-1 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <Link
          to={`/aneel/c/${encodeURIComponent(row.case_key)}`}
          className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {row.notice_number}
        </Link>
        <span className="text-xs text-muted-foreground">{row.entity_name}</span>
        {/* Carried on the row, not in a caption: ANEEL delegates enforcement to
            state agencies, so a difference in outcome may follow from who decided
            it rather than from how the conduct was treated. */}
        {row.cross_agency ? (
          <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[0.65rem] text-foreground">
            {t('aneel:case.comparables.crossAgency', { agency: row.inspecting_agency })}
          </span>
        ) : null}
      </div>
      <p className="text-sm text-foreground">
        {row.trajectory
          ? t(`aneel:overview.trajectories.${row.trajectory}`, row.trajectory)
          : t('aneel:overview.trajectories.UNCLASSIFIED')}
        {row.original_amount !== null ? ` · ${currency.format(row.original_amount)}` : ''}
        {row.final_amount !== null ? ` → ${currency.format(row.final_amount)}` : ''}
        {row.decided_on ? (
          <span className="ml-2 text-xs text-muted-foreground">
            {t('aneel:case.comparables.decidedOn', { date: row.decided_on })}
          </span>
        ) : null}
      </p>
      {row.shared_bands.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('aneel:case.comparables.shared', { bands: row.shared_bands.join('; ') })}
        </p>
      ) : null}
    </li>
  );
}

/**
 * The way into the company profile.
 *
 * Only linked on a full 14-digit CNPJ: a prefix would resolve to an arbitrary
 * one of several companies, and a profile opened for the wrong company is
 * indistinguishable from a correct one.
 */
function entityHref(cnpj: string | null): string | undefined {
  return cnpj && cnpj.length === 14 ? `/aneel/e/${cnpj}` : undefined;
}

function Row({
  label,
  value,
  href,
}: {
  label: string;
  value: string | null;
  /** Set to make the value a link. Omitted rows stay plain text. */
  href?: string;
}) {
  return (
    <>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mb-1 text-sm text-foreground">
        {value === null ? (
          '—'
        ) : href ? (
          <Link to={href} className="text-primary underline-offset-2 hover:underline">
            {value}
          </Link>
        ) : (
          value
        )}
      </dd>
    </>
  );
}

function BackLink() {
  const { t } = useTranslation(['aneel']);
  return (
    <Link
      to="/aneel/cases"
      className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      {t('aneel:case.backToList')}
    </Link>
  );
}
